import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Download,
  Calendar,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  RefreshCw,
  ChevronDown,
  User,
  Phone,
  ArrowRight,
  Search,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { fetchExtensions } from '@/services/api';
import { loadCallDetail } from '@/lib/reports/extensionReports';
import { ScheduleReportButton } from './ScheduleReportButton';
import type { Extension, CallRecord, MonthlyCallData } from '@/types';
import toast from 'react-hot-toast';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { buildCallDetailPdf } from './extensionPdfs';
import { MonthlyChart } from './MonthlyChart';
import { ContactNameLine } from '@/components/crm/CallerName';
import { compareExtensions, getDatePresetRange, type DatePreset } from './reportDates';

/** Stack multi-part From/To values (e.g. name + number) onto separate lines. */
export function PartyLines({ value }: { value?: string }) {
  if (!value || value === '-') {
    return <span className="text-sm text-gray-400">—</span>;
  }
  const raw = value.trim();
  // Split on common PBX separators while keeping phone-like tokens intact
  const parts = raw
    .split(/\s*[|/·•]\s*|\s{2,}|\s+<\s*|\s*>\s*/)
    .map((p) => p.replace(/^<|>$/g, '').trim())
    .filter(Boolean);

  const lines: string[] = [];
  const seen = new Set<string>();
  for (const part of parts.length ? parts : [raw]) {
    const digits = part.replace(/[^\d+]/g, '');
    // Drop exact digit duplicates (same number repeated)
    if (digits && seen.has(digits)) continue;
    if (digits) seen.add(digits);
    if (!lines.includes(part)) lines.push(part);
  }

  // If still one long space-separated string with 2+ phone-ish tokens, stack them
  if (lines.length === 1) {
    const tokens = lines[0].split(/\s+/).filter(Boolean);
    const phoneish = tokens.filter((t) => /[\d+]{3,}/.test(t));
    if (phoneish.length >= 2 && phoneish.length === tokens.length) {
      const uniq: string[] = [];
      const seenT = new Set<string>();
      for (const t of phoneish) {
        const d = t.replace(/[^\d+]/g, '');
        if (seenT.has(d)) continue;
        seenT.add(d);
        uniq.push(t);
      }
      return (
        <div className="text-sm font-mono text-gray-900 dark:text-white leading-snug">
          {uniq.map((line) => (
            <div key={line} className="truncate" title={line}>
              {line}
            </div>
          ))}
        </div>
      );
    }
  }

  return (
    <div className="text-sm font-mono text-gray-900 dark:text-white leading-snug">
      {lines.map((line) => (
        <div key={line} className="truncate" title={line}>
          {line}
        </div>
      ))}
    </div>
  );
}

export function ExtensionDetailReport() {
  const { session } = useModuleAccess('reporting');
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingExtensions, setLoadingExtensions] = useState(true);
  const [callRecords, setCallRecords] = useState<CallRecord[]>([]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showExtensionDropdown, setShowExtensionDropdown] = useState(false);
  const [extensionSearch, setExtensionSearch] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState<DatePreset | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [monthlyData, setMonthlyData] = useState<MonthlyCallData[]>([]);

  useEffect(() => {
    loadExtensions();
  }, []);

  const loadExtensions = async () => {
    try {
      setLoadingExtensions(true);
      const exts = await fetchExtensions();
      setExtensions([...exts].sort(compareExtensions));
    } catch (error: any) {
      toast.error('Failed to load extensions');
      console.error(error);
    } finally {
      setLoadingExtensions(false);
    }
  };

  const filteredExtensions = useMemo(() => {
    const query = extensionSearch.trim().toLowerCase();
    if (!query) return extensions;

    return extensions.filter((extension) =>
      [
        extension.number,
        extension.display_name,
        extension.username,
        extension.ext_name,
        extension.first_name,
        extension.last_name,
      ].some((value) => value?.toLowerCase().includes(query))
    );
  }, [extensionSearch, extensions]);

  const applyDatePreset = (preset: DatePreset) => {
    const range = getDatePresetRange(preset);
    setActiveDatePreset(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const formatDateTime = (timeStr: string, timestamp?: number): string => {
    try {
      if (timestamp) {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
      }
      return new Date(timeStr).toLocaleString();
    } catch {
      return timeStr;
    }
  };

  const formatDuration = (seconds: number): string => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const formatDurationHMS = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getDispositionStyle = (disposition: string) => {
    const d = disposition?.toLowerCase() || '';
    if (d.includes('no answer') || d.includes('noanswer') || d.includes('missed')) {
      return {
        icon: PhoneMissed,
        color: 'text-orange-600 dark:text-orange-400',
        bg: 'bg-orange-100 dark:bg-orange-900/30',
      };
    }
    if (d.includes('answer')) {
      return {
        icon: PhoneIncoming,
        color: 'text-green-600 dark:text-green-400',
        bg: 'bg-green-100 dark:bg-green-900/30',
      };
    }
    if (d.includes('busy')) {
      return {
        icon: Phone,
        color: 'text-orange-600 dark:text-orange-400',
        bg: 'bg-orange-100 dark:bg-orange-900/30',
      };
    }
    if (d.includes('failed') || d.includes('cancel')) {
      return {
        icon: PhoneMissed,
        color: 'text-gray-600 dark:text-gray-400',
        bg: 'bg-gray-100 dark:bg-gray-900/30',
      };
    }
    return {
      icon: Phone,
      color: 'text-primary-600 dark:text-primary-400',
      bg: 'bg-primary-100 dark:bg-primary-900/30',
    };
  };

  const getCallTypeStyle = (type: string) => {
    switch (type) {
      case 'Inbound':
        return { icon: PhoneIncoming, color: 'text-green-600', label: 'Inbound' };
      case 'Outbound':
        return { icon: PhoneOutgoing, color: 'text-primary-600', label: 'Outbound' };
      default:
        return { icon: Phone, color: 'text-purple-600', label: 'Internal' };
    }
  };

  const generateReport = async () => {
    if (!selectedExtension) {
      toast.error('Please select an extension');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    setHasGenerated(false);
    setMonthlyData([]);

    try {
      const { records, monthlyData: monthly } = await loadCallDetail(selectedExtension, startDate, endDate);
      setCallRecords(records);
      setMonthlyData(monthly);
      setHasGenerated(true);

      if (records.length === 0) {
        toast('No call records found for this period', { icon: 'ℹ️' });
      } else {
        toast.success(`Found ${records.length} call records`);
      }
    } catch (error: any) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPdf = async () => {
    if (callRecords.length === 0) return;

    setExportingPdf(true);
    try {
      const extName =
        selectedExtension?.display_name || selectedExtension?.username || 'Unknown';
      const pdf = buildCallDetailPdf({
        extensionLabel: `${selectedExtension?.number ?? ''} - ${extName}`,
        startDate,
        endDate,
        records: callRecords,
        monthlyData,
        formatTime: (record) => formatDateTime(record.time, record.timestamp),
        customerName: session?.customer?.name,
        assets: await loadPdfAssets(),
      });

      const extNameFile =
        selectedExtension?.display_name || selectedExtension?.number || 'extension';
      const fileName = `Extension_Detail_Report_${extNameFile}_${startDate}_to_${endDate}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error: any) {
      toast.error('Failed to export PDF');
      console.error(error);
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Report Configuration Card */}
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
            <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Extension Detail Report
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              View detailed call records for any extension
            </p>
          </div>
          {selectedExtension && (
            <ScheduleReportButton
              kind="detail"
              className="ml-auto"
              filters={{ extension: selectedExtension.number }}
              name={`Call Detail ${selectedExtension.number}`}
              dateRange={activeDatePreset ?? undefined}
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Extension Selector */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Extension
            </label>
            <button
              type="button"
              onClick={() => {
                setShowExtensionDropdown(!showExtensionDropdown);
                if (showExtensionDropdown) setExtensionSearch('');
              }}
              disabled={loadingExtensions}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-left text-gray-900 dark:text-white hover:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
            >
              {loadingExtensions ? (
                <span className="text-gray-400">Loading...</span>
              ) : selectedExtension ? (
                <span>
                  {selectedExtension.number} -{' '}
                  {selectedExtension.display_name || selectedExtension.username}
                </span>
              ) : (
                <span className="text-gray-400">Select extension...</span>
              )}
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${showExtensionDropdown ? 'rotate-180' : ''}`}
              />
            </button>
            {showExtensionDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute z-20 mt-1 w-full min-w-[280px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden"
              >
                <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="search"
                      autoFocus
                      value={extensionSearch}
                      onChange={(event) => setExtensionSearch(event.target.value)}
                      placeholder="Search extension..."
                      className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {filteredExtensions.length ? (
                    filteredExtensions.map((ext) => (
                      <button
                        type="button"
                        key={ext.id}
                        className={`block w-full px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          selectedExtension?.id === ext.id
                            ? 'bg-purple-50 dark:bg-purple-900/30'
                            : ''
                        }`}
                        onClick={() => {
                          setSelectedExtension(ext);
                          setShowExtensionDropdown(false);
                          setExtensionSearch('');
                        }}
                      >
                        <span className="text-gray-900 dark:text-white font-medium">
                          {ext.number}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                          {ext.display_name || ext.username}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No extensions found
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setActiveDatePreset(null);
                setStartDate(e.target.value);
              }}
              className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setActiveDatePreset(null);
                setEndDate(e.target.value);
              }}
              className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Generate Button */}
          <div className="flex items-end">
            <Button
              variant="primary"
              onClick={generateReport}
              isLoading={loading}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
            Quick select:
          </span>
          {(
            [
              ['thisMonth', 'This Month'],
              ['lastMonth', 'Last Month'],
              ['thisYear', 'This Year'],
              ['lastYear', 'Last Year'],
            ] as Array<[DatePreset, string]>
          ).map(([preset, label]) => (
            <button
              key={preset}
              type="button"
              onClick={() => applyDatePreset(preset)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeDatePreset === preset
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Loader size="lg" />
            <span className="ml-4 text-gray-600 dark:text-gray-400">
              Fetching call records...
            </span>
          </div>
        </Card>
      )}

      {/* Report Results */}
      {!loading && hasGenerated && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            {/* Report Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Call Records
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {callRecords.length} records found for {selectedExtension?.number} -{' '}
                  {selectedExtension?.display_name || selectedExtension?.username}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={exportToPdf}
                isLoading={exportingPdf}
                disabled={callRecords.length === 0}
                className="pdf-hide bg-purple-600 hover:bg-purple-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>

            {/* Summary Stats */}
            {callRecords.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Calls</p>
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {callRecords.length}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Inbound</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {callRecords.filter((r) => r.call_type === 'Inbound').length}
                  </p>
                </div>
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Outbound</p>
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {callRecords.filter((r) => r.call_type === 'Outbound').length}
                  </p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Missed/Failed
                  </p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {
                      callRecords.filter((r) => {
                        const d = r.disposition?.toLowerCase() || '';
                        return (
                          d.includes('no answer') ||
                          d.includes('noanswer') ||
                          d.includes('missed') ||
                          d.includes('failed') ||
                          d.includes('cancel')
                        );
                      }).length
                    }
                  </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Time</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {formatDurationHMS(
                      callRecords.reduce((sum, r) => sum + (r.talk_duration || 0), 0)
                    )}
                  </p>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Avg Time/Call
                  </p>
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {(() => {
                      const answered = callRecords.filter(
                        (r) => (r.talk_duration || 0) > 0
                      );
                      const total = answered.reduce(
                        (sum, r) => sum + (r.talk_duration || 0),
                        0
                      );
                      return formatDurationHMS(
                        answered.length > 0 ? Math.round(total / answered.length) : 0
                      );
                    })()}
                  </p>
                </div>
              </div>
            )}

            {/* Monthly Activity (Current + Previous 2 Months) */}
            {monthlyData.length > 0 && (
              <div className="space-y-6 mb-6">
                <Card>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                    Monthly Activity Comparison (Current + Previous 2 Months)
                  </h4>
                  <MonthlyChart data={monthlyData} />
                </Card>
                <Card>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Monthly Breakdown (Current + Previous 2 Months)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                          <th
                            rowSpan={2}
                            className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300 align-bottom"
                          >
                            Month
                          </th>
                          <th
                            colSpan={3}
                            className="text-center py-2 px-3 text-sm font-semibold text-green-700 dark:text-green-400 border-b border-gray-200 dark:border-gray-700"
                          >
                            Inbound
                          </th>
                          <th
                            colSpan={3}
                            className="text-center py-2 px-3 text-sm font-semibold text-primary-700 dark:text-primary-400 border-b border-gray-200 dark:border-gray-700"
                          >
                            Outbound
                          </th>
                          <th
                            rowSpan={2}
                            className="text-center py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300 align-bottom"
                          >
                            Total Talk
                            <br />
                            Duration
                          </th>
                        </tr>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                            Answered
                          </th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                            No Answer
                          </th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                            Total
                          </th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                            Answered
                          </th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                            No Answer
                          </th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.map((month, index) => (
                          <motion.tr
                            key={month.monthKey}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          >
                            <td className="py-3 px-3 font-medium text-gray-900 dark:text-white">
                              {month.month}
                            </td>
                            <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                              {month.inboundAnswered}
                            </td>
                            <td className="py-3 px-2 text-center text-orange-500 dark:text-orange-400">
                              {month.inboundNoAnswer}
                            </td>
                            <td className="py-3 px-2 text-center font-medium text-gray-700 dark:text-gray-300">
                              {month.inboundTotal}
                            </td>
                            <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                              {month.outboundAnswered}
                            </td>
                            <td className="py-3 px-2 text-center text-orange-500 dark:text-orange-400">
                              {month.outboundNoAnswer}
                            </td>
                            <td className="py-3 px-2 text-center font-medium text-gray-700 dark:text-gray-300">
                              {month.outboundTotal}
                            </td>
                            <td className="py-3 px-3 text-center font-medium text-gray-900 dark:text-white">
                              {formatDurationHMS(month.totalTalkDuration)}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                          <td className="py-3 px-3 text-gray-900 dark:text-white">
                            Total
                          </td>
                          <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                            {monthlyData.reduce((sum, m) => sum + m.inboundAnswered, 0)}
                          </td>
                          <td className="py-3 px-2 text-center text-orange-500 dark:text-orange-400">
                            {monthlyData.reduce((sum, m) => sum + m.inboundNoAnswer, 0)}
                          </td>
                          <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">
                            {monthlyData.reduce((sum, m) => sum + m.inboundTotal, 0)}
                          </td>
                          <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                            {monthlyData.reduce((sum, m) => sum + m.outboundAnswered, 0)}
                          </td>
                          <td className="py-3 px-2 text-center text-orange-500 dark:text-orange-400">
                            {monthlyData.reduce((sum, m) => sum + m.outboundNoAnswer, 0)}
                          </td>
                          <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">
                            {monthlyData.reduce((sum, m) => sum + m.outboundTotal, 0)}
                          </td>
                          <td className="py-3 px-3 text-center text-gray-900 dark:text-white">
                            {formatDurationHMS(
                              monthlyData.reduce((sum, m) => sum + m.totalTalkDuration, 0)
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            {/* Call Records Table */}
            {callRecords.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 max-w-[7.5rem]">
                        From
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        To
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {callRecords.map((record, index) => {
                      const dispositionStyle = getDispositionStyle(record.disposition);
                      const typeStyle = getCallTypeStyle(record.call_type);
                      const TypeIcon = typeStyle.icon;
                      const StatusIcon = dispositionStyle.icon;

                      return (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.01 }}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800/30"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900 dark:text-white">
                                {formatDateTime(record.time, record.timestamp)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <TypeIcon className={`w-4 h-4 ${typeStyle.color} mr-2`} />
                              <span className={`text-sm font-medium ${typeStyle.color}`}>
                                {record.call_type}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-3 w-28 max-w-[7.5rem] align-top">
                            <ContactNameLine value={record.call_from} />
                            <PartyLines value={record.call_from} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <ArrowRight className="w-3 h-3 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900 dark:text-white font-mono">
                                {record.call_to || '-'}
                                <ContactNameLine value={record.call_to} />
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${dispositionStyle.bg} ${dispositionStyle.color}`}
                            >
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {record.disposition || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-white">
                              {formatDuration(record.talk_duration || 0)}
                            </span>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No call records found for the selected period.
                </p>
              </div>
            )}

            {/* Total Duration */}
            {callRecords.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-end">
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total Talk Time
                    </p>
                    <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                      {formatDurationHMS(
                        callRecords.reduce((sum, r) => sum + (r.talk_duration || 0), 0)
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      )}
    </div>
  );
}
